import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "zavifc-install-dismissed";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 14; // 14 days

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

const isIos = () =>
  typeof navigator !== "undefined" &&
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !/crios|fxios|edgios/i.test(navigator.userAgent);

export const InstallPrompt = () => {
  const { pathname } = useLocation();
  // Parent portal has its own always-on install gate (PortalInstallGate),
  // so hide this generic prompt when the user is inside /portal.
  const suppressed = pathname.startsWith("/portal");
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;

    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    // iOS Safari never fires beforeinstallprompt — show a manual hint after 4s
    if (isIos()) {
      const t = setTimeout(() => {
        setShowIos(true);
        setVisible(true);
      }, 4000);
      return () => {
        window.removeEventListener("beforeinstallprompt", onBip);
        clearTimeout(t);
      };
    }

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice.catch(() => ({ outcome: "dismissed" }));
    if (outcome === "accepted") {
      setVisible(false);
      setDeferred(null);
    } else {
      dismiss();
    }
  };

  if (!visible || suppressed) return null;

  return (
    <div
      className="fixed z-[60] left-3 right-3 bottom-3 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm border border-[#D4AF37]/40 bg-[#0A0A0A]/95 backdrop-blur-md p-4 shadow-2xl"
      data-testid="install-prompt"
      role="dialog"
      aria-label="Install Zavi FC app"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-white/50 hover:text-white p-1"
        aria-label="Dismiss install prompt"
        data-testid="install-prompt-close"
      >
        <X size={14} />
      </button>
      <div className="flex items-start gap-3">
        <img src="/pwa-192.png" alt="" className="w-11 h-11 shrink-0 border border-[#D4AF37]/30" />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">Install Zavi FC</p>
          <p className="text-white/70 text-xs mt-1 leading-relaxed">
            {showIos
              ? <>Tap <span className="text-white font-semibold">Share</span> then <span className="text-white font-semibold">Add to Home Screen</span> to install.</>
              : "Get the full-screen app on your home screen for faster access to attendance, dues and reports."}
          </p>
          {!showIos && (
            <button
              onClick={install}
              className="mt-3 inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#F3D059] text-black font-bold uppercase tracking-widest text-[10px] px-4 py-2"
              data-testid="install-prompt-cta"
            >
              <Download size={12} /> Install App
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
